import { useEffect, useRef, useState, type ReactNode, type FormEvent } from 'react';
import { useModal } from '@/lib/ModalContext';
import { submit, captureLead, type FormKind, type LeadFields } from '@/lib/api';
import {
  TenantThemeProvider, fetchPublicTenant, tenantIntakeQuestions,
  DEFAULT_TENANT, type TenantConfig,
} from '@/lib/tenant';
import IntakeFlow, { type IntakeFlowHandle } from './IntakeFlow';
import styles from './Modal.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Modal({ kind, children }: { kind: FormKind; children: ReactNode }) {
  const { open, setOpen } = useModal();
  const isOpen = open === kind;
  return (
    <div
      className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}
    >
      <div className={styles.modal}>
        <button className={styles.close} aria-label="Close" onClick={() => setOpen(null)}>×</button>
        {children}
      </div>
    </div>
  );
}

export function WaitlistModal() {
  const { setOpen } = useModal();
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState('submitting');
    const fd = new FormData(e.currentTarget);
    const r = await submit('waitlist', Object.fromEntries(fd));
    if (r.ok) {
      setState('success'); setMessage(r.message);
      (e.target as HTMLFormElement).reset();
    } else {
      setState('error'); setMessage(`Couldn't submit (${r.error}). Email hello@avowal.ai instead.`);
    }
    setTimeout(() => { if (state !== 'submitting') setOpen(null); }, 1800);
  }

  return (
    <Modal kind="waitlist">
      <div className={styles.eyebrow}>/ Waitlist</div>
      <h3 className={styles.title}>Join the waitlist</h3>
      <p className={styles.sub}>
        Get priority access when private beta opens. We'll only email you when the doors open or when something materially changes.
      </p>
      <form onSubmit={onSubmit}>
        <div className={styles.field}>
          <label htmlFor="w-email">Email</label>
          <input type="email" id="w-email" name="email" required autoComplete="email" placeholder="you@example.com" />
        </div>
        <div className={styles.field}>
          <label htmlFor="w-state">State (optional)</label>
          <input type="text" id="w-state" name="state" autoComplete="address-level1" placeholder="CT" />
        </div>
        <div className={styles.field}>
          <label htmlFor="w-role">I am a&hellip;</label>
          <select id="w-role" name="role" defaultValue="potential_client">
            <option value="potential_client">Potential client</option>
            <option value="attorney">Licensed attorney</option>
            <option value="press">Press / analyst</option>
            <option value="other">Other</option>
          </select>
        </div>
        <button type="submit" className={styles.submit} disabled={state === 'submitting'}>
          {state === 'submitting' ? 'Submitting…' : 'Join waitlist →'}
        </button>
        {state === 'success' && <div className={`${styles.msg} ${styles.success}`}>{message}</div>}
        {state === 'error'   && <div className={`${styles.msg} ${styles.error}`}>{message}</div>}
      </form>
    </Modal>
  );
}

/**
 * Public intake → quote front door. Hosts the multi-step <IntakeFlow> inside the
 * shared modal shell, under a <TenantThemeProvider> resolved from the current
 * host (white-label: the client reaches this on the attorney's own domain, so
 * the tenant is resolved by domain, not session). Retains abandoned-lead capture
 * on close via the shared IntakeFlowHandle.
 */
export function IntakeModal() {
  const { open, setOpen } = useModal();
  const isOpen = open === 'intake';
  const [tenant, setTenant] = useState<TenantConfig>(DEFAULT_TENANT);
  // The flow updates this on every change / successful submit; the close effect
  // reads it to persist a partial lead. Reset each time the modal opens.
  const handleRef = useRef<IntakeFlowHandle>({ fields: { email: '' }, done: false });
  // Remount the flow (fresh state) on each open via an incrementing key.
  const [instance, setInstance] = useState(0);

  // Resolve the tenant lazily the first time the modal is opened (public,
  // graceful — always falls back to AVOWAL/ELARIA/stock questions).
  useEffect(() => {
    if (!isOpen) return;
    let live = true;
    void fetchPublicTenant().then((t) => { if (live) setTenant(t); });
    return () => { live = false; };
  }, [isOpen]);

  // Abandoned-lead capture: when the modal closes with a valid email entered but
  // no successful submit, persist whatever was filled as a draft so the lead
  // survives. Best-effort and fire-and-forget — never blocks the close.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      const h = handleRef.current;
      if (!h.done && EMAIL_RE.test((h.fields.email ?? '').trim())) {
        void captureLead(h.fields as LeadFields, false);
      }
    }
    if (isOpen) { handleRef.current = { fields: { email: '' }, done: false }; setInstance((n) => n + 1); }
    wasOpen.current = isOpen;
  }, [isOpen]);

  return (
    <Modal kind="intake">
      <TenantThemeProvider tenant={tenant}>
        <IntakeFlow
          key={instance}
          questions={tenantIntakeQuestions(tenant)}
          handleRef={handleRef}
          onClose={() => setOpen(null)}
        />
      </TenantThemeProvider>
    </Modal>
  );
}

// Mount both modals once at app root; they show themselves based on context.
export function Modals() {
  // Add Esc + scroll-lock effects scoped at this top-level mount.
  const { open } = useModal();
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  return <><WaitlistModal /><IntakeModal /></>;
}
